import { dispatch } from 'store'
import store from 'store'
import { IAIImageSocketMessage } from 'types/aiImage'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { updateMediaItemWithAIImageData } from 'utils/aiImage'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import { creditsThunks } from 'store/features/credits/thunks'
import aiAvatarSlice from 'store/features/aiAvatar/slice'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import {
  clearAssetPendingByImageId,
  removePendingSourceImageEntry,
  storeAssetResultByImageId
} from 'store/features/aiAvatar/sourceImageStorage'
import { getSourceImageProgressId } from 'store/features/aiAvatar/utils'
import { MediaStatus } from 'types/media'
import { ErrorKeys } from 'consts/errorKeys'
import { handleApiError } from 'store/utils'
import { generationProgressService } from 'services/generationProgress'

const { actions: aiAvatarActions } = aiAvatarSlice

// The AI Avatar "Enhance quality" / "Resize with AI" ops reframe the source
// image through the SAME ai-image pipeline, so their result arrives here — but
// they have NO timeline media item, no generation-progress entry, and are not
// AI-Library entries. They're correlated purely by `pendingSourceImageId` in the
// aiAvatar slice. Handling them and RETURNING before the timeline/history/
// progress path keeps those side effects (INV-AIMG-5 library, progress service)
// untouched for a source-image op. Updates go through plain aiAvatar slice
// actions, so they're inherently non-undoable (INV-SOCK-3). Returns true when it
// consumed the message.
// Known gap (INV-AIMG-1): `handlePastSocketDataByType` has no `ai-image` branch,
// so an op that finishes during a disconnect isn't replayed on reconnect —
// same as timeline images today. For a FRESH form (case 1) the op is persisted
// to localStorage and reconciled against `user-images-by-ids` on the next AI
// Avatar tab mount (`restorePendingSourceImage`), so a reload recovers it; the
// terminal branches below must drop that entry alongside the redux correlation.
const handleAvatarSourceImageCompletion = (
  data: IAIImageSocketMessage
): boolean => {
  const state = store.getState()
  const pendingId = aiAvatarSelectors.pendingSourceImageId(state)
  // An op can be armed in the form's SLOT (the open form — case 1 or 2), in
  // the assetId-keyed ops MAP (a regen clip's op whose form was closed — case
  // 2 only), or both (open regen form). Either match consumes the message.
  const isSlotMatch = pendingId !== null && data.imageId === pendingId

  const { error, filePath, width, height } = data
  if (error) {
    // Progress terminal by the op's namespaced generator id — covers slot,
    // map, AND abandoned (case-1 walked-away) ops; a no-op for every other
    // ai-image message (no such generator).
    generationProgressService.stop({
      mediaItemId: getSourceImageProgressId(data.imageId),
      shouldRemoveFromStorage: true
    })
    // Map terminal first — it matches by imageId regardless of form state.
    const isMapMatch = clearAssetPendingByImageId(data.imageId)
    if (!isSlotMatch && !isMapMatch) return false
    if (isSlotMatch) {
      dispatch(aiAvatarActions.setAvatarLoading(false))
      dispatch(aiAvatarActions.clearPendingSourceImage())
      removePendingSourceImageEntry()
    }
    dispatch(handleApiError(ErrorKeys.AI_IMAGE_GENERATION_FAILED))
    return true
  }
  // Same completion gate as the timeline image path (INV-AIMG-2): only apply on
  // a fully-resolved frame.
  if (filePath && width && height) {
    // Progress terminal (same no-op rule as the error path above).
    generationProgressService.complete({
      mediaItemId: getSourceImageProgressId(data.imageId)
    })
    // Store the clip's result in the ops map (pending → result, committed
    // ratio taken from the map's own pending op) so a later reselect of the
    // clip shows it — for the open-form case IN ADDITION to the form apply
    // below, for the closed-form case as the sole destination. No-op for a
    // case-1 op (no map entry).
    const isMapMatch = storeAssetResultByImageId(data.imageId, {
      url: filePath,
      width,
      height
    })
    if (!isSlotMatch && !isMapMatch) return false
    if (isSlotMatch) {
      // One atomic action: snapshots the replaced image for one-step undo,
      // applies the result, commits Resize's target ratio (null for Enhance =
      // keep), and releases the loader + correlation.
      dispatch(
        aiAvatarActions.applySourceImageResult({
          url: filePath,
          width,
          height,
          aspectRatio: aiAvatarSelectors.pendingSourceAspectRatio(state)
        })
      )
      removePendingSourceImageEntry()
    }
    dispatch(creditsThunks.getUserCredits())
    return true
  }
  // Incomplete frame: consume only a slot-armed op's message (keep waiting,
  // as before); a gate-failed message with no slot match falls through — the
  // main path's own gates make it a no-op for unknown ids.
  return isSlotMatch
}

export const handleAIImgSocketData = (data: IAIImageSocketMessage) => {
  // Avatar source-image ops (Enhance/Resize) short-circuit here — they have no
  // timeline item, so they must not fall through to the media/history path.
  if (handleAvatarSourceImageCompletion(data)) return

  const state = store.getState()

  const { width: canvasWidth, height: canvasHeight } =
    projectDataSelectors.defaultDimensions(state)
  const pendingAiImgMediaItems =
    projectDataSelectors.pendingAiImgMediaItems(state)
  const currentMediaItems = projectDataSelectors.mediaItems(state)

  const { imageId, error, filePath, width, height } = data

  const assetIdStr = imageId.toString()
  // Prefer the present-state item so the user's in-flight resize is
  // captured; fall back to the pending lookup (covers past/future) only
  // for tracking/error handling.
  const mediaItem =
    currentMediaItems.find(item => item.assetId === assetIdStr) ||
    pendingAiImgMediaItems.find(item => item.assetId === assetIdStr)
  // no matter if the item was deleted from timeline, we need to update the history and progress
  if (error) {
    dispatch(aiImageThunks.deleteHistoryItem(imageId))
    generationProgressService.stop({
      mediaItemId: mediaItem?.id,
      assetId: String(imageId),
      shouldRemoveFromStorage: true
    })
  }
  if (filePath && width && height) {
    dispatch(
      aiImageThunks.updateHistoryItem(imageId, {
        filePath,
        thumbnailPath: filePath,
        id: imageId,
        status: MediaStatus.OK
      })
    )
  }

  if (mediaItem) {
    if (error) {
      dispatch(handleApiError(ErrorKeys.AI_IMAGE_GENERATION_FAILED))
      dispatch(projectDataThunks.removeVisualMediaItem(mediaItem.id))
      return
    }

    if (filePath && width && height) {
      generationProgressService.complete({
        mediaItemId: mediaItem.id,
        assetId: mediaItem.assetId
      })
      dispatch(
        projectDataThunks.updateMediaAfterGeneration(
          updateMediaItemWithAIImageData(mediaItem, filePath, width, height, {
            width: canvasWidth,
            height: canvasHeight
          }),
          {
            isUndoable: false
          }
        )
      )

      dispatch(creditsThunks.getUserCredits())
    }
  }
}
